import type {
  Segment,
  SegmentCollection,
  SegmentFragment,
} from '@ve-pro/types/track.type'
import { getGroupBySegment, isSegmentGroupChild } from './segment-group'
import { SegmentType } from '@ve-pro/enums/segment'
import { useTrackStore, useHistoryStore } from '@ve-pro/store'
import { isLangSegment, removeSegment } from '../tracks-actions'
import { updateMainTrack } from '../update-main-track'
import { getClosestUnLangSegment } from '@ve-pro/store/project/languages'

export function isSegmentFragment(
  segment: Segment,
): segment is SegmentFragment {
  return segment.type === SegmentType.Fragment
}

/**
 * 判断是否是片段的子元件
 * @param segment 当前判断元件
 * @param id 非必填，当传的时候，会判断当前元件是否归属在该片段 id 下
 */
export function isSegmentFragmentChild<T extends SegmentCollection>(
  segment: T,
  id?: SegmentFragment['id'], // 如果有传 id， 则判断该元件是否属于这个片段之下
): segment is T {
  let s: Segment = segment

  if (isSegmentGroupChild(segment)) {
    const group = getGroupBySegment(segment)
    if (group) {
      s = group
    }
  }

  if (typeof id === 'number') {
    return (
      typeof s.fragment_segment_id === 'number' && s.fragment_segment_id === id
    )
  }

  return typeof s.fragment_segment_id === 'number' && s.fragment_segment_id > 0
}

export function isSegmentFragmentChildForLangSegment<
  T extends SegmentCollection,
>(
  segment: T,
  id?: SegmentFragment['id'], // 如果有传 id， 则判断该元件是否属于这个片段之下
): segment is T {
  return isSegmentFragmentChild(getClosestUnLangSegment(segment) ?? segment, id)
}

export function getSegmentFragmentById(
  id: SegmentFragment['id'],
): SegmentFragment | undefined {
  const trackStore = useTrackStore()

  return trackStore.defaultFragments.find((f) => f.id === id)
}

export async function quitFragmentEdit(options?: {
  pushHistory?: boolean
}): Promise<any> {
  const trackStore = useTrackStore()
  const historyStore = useHistoryStore()

  const { pushHistory = true } = options ?? {}

  // 检查片段是否为空，空的话删除当前的片段
  const emptyFragments = trackStore.defaultFragments.filter((f) => {
    const tracks = trackStore.getTracksByFragmentSegmentId(f.id)
    const segments = tracks.reduce((acc, track) => {
      acc.push(...track.segments)

      return acc
    }, [] as any[])

    return segments.length === 0
  })
  if (emptyFragments.length > 0) {
    for (let index = 0; index < emptyFragments.length; index++) {
      const emptyFragment = emptyFragments[index]!
      await removeSegment(emptyFragment)
    }

    updateMainTrack(true, {
      mainTrack: trackStore.defaultExcludeFragmentMainTrack ?? undefined,
    })
  }
  trackStore.activeSegmentFragment = null

  if (pushHistory) {
    historyStore.push()
  }
}

/**
 * 获取元件的 fragment_segment_id ，如果是裂变的元件，fragment_segment_id 都为 0，所以需要通过裂变的原元件去拿fragment_segment_id
 * @param segment
 */
export function getFragmentSegmentIdBySegment<T extends SegmentCollection>(
  segment: T,
): Segment['fragment_segment_id'] {
  if (isLangSegment(segment)) {
    return (getClosestUnLangSegment(segment) || segment).fragment_segment_id
  }
  return segment.fragment_segment_id
}

export function addLangFragmentSegmentIdToFragmentSegmentId<
  T extends SegmentCollection,
>(segment: T): T {
  return {
    ...segment,
    fragment_segment_id: getFragmentSegmentIdBySegment(segment),
  }
}
